import { DOCUMENT } from "@angular/common";
import { HttpBackend, HttpClient } from "@angular/common/http";
import { EnvironmentProviders, Injectable, inject, provideAppInitializer } from "@angular/core";
import { firstValueFrom } from "rxjs";

/** Relative to <base href>, so it is same-origin standalone and inside the CXone iframe. */
export const ICON_SPRITE_URL = "assets/icons/lyra-sprite.svg";
export const ICON_SPRITE_ELEMENT_ID = "esp-lyra-icon-sprite";

/**
 * Injects the bundled Lyra icon sprite (generated by scripts/build-icon-sprite.mjs)
 * into <body> once, so `<use href="#lyra-...">` resolves for every <esp-icon>.
 * The CXone CDN sprite cannot be used: ESP runs on its own origin and
 * cross-origin <use> references are blocked.
 */
@Injectable({ providedIn: "root" })
export class IconSpriteLoaderService {
  private readonly document = inject(DOCUMENT);
  // HttpBackend skips the app interceptors (auth headers, request-success
  // dispatch): this is a static asset, not an API call.
  private readonly http = new HttpClient(inject(HttpBackend));
  private loading?: Promise<boolean>;

  /** Resolves true when the sprite is in the DOM; false (logged) on failure. Never rejects. */
  load(): Promise<boolean> {
    if (!this.loading) {
      this.loading = this.fetchAndInject();
    }
    return this.loading;
  }

  private async fetchAndInject(): Promise<boolean> {
    if (this.document.getElementById(ICON_SPRITE_ELEMENT_ID)) {
      return true;
    }
    try {
      const markup = await firstValueFrom(this.http.get(ICON_SPRITE_URL, { responseType: "text" }));
      const parsed = new DOMParser().parseFromString(markup, "image/svg+xml");
      const svg = parsed.documentElement;
      if (svg.nodeName.toLowerCase() !== "svg" || parsed.getElementsByTagName("parsererror").length) {
        throw new Error("response is not an SVG document");
      }
      const sprite = this.document.importNode(svg, true) as unknown as SVGSVGElement;
      sprite.id = ICON_SPRITE_ELEMENT_ID;
      sprite.setAttribute("aria-hidden", "true");
      sprite.setAttribute("focusable", "false");
      sprite.style.display = "none";
      this.document.body.insertBefore(sprite, this.document.body.firstChild);
      return true;
    } catch (error) {
      // Icons are decorative or backed by aria-labels; the app keeps working without them.
      console.error(`ESP icons: could not load ${ICON_SPRITE_URL}`, error);
      this.loading = undefined;
      return false;
    }
  }
}

/** App initializer that starts loading the icon sprite without delaying bootstrap. */
export function provideIconSprite(): EnvironmentProviders {
  return provideAppInitializer(() => {
    void inject(IconSpriteLoaderService).load();
  });
}
